import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, Eye, GitFork, ExternalLink } from "lucide-react";
import { ShowcaseItem } from '@/types/showcase';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

interface AgentCardProps {
  item: ShowcaseItem;
  onLike?: (id: string) => void;
  onFork?: (id: string) => void;
  onView?: (id: string) => void;
  isLiked?: boolean;
  className?: string;
  viewOnly?: boolean;
}

/**
 * Card component for displaying agent preview in the showcase gallery
 */
export const AgentCard: React.FC<AgentCardProps> = ({
  item,
  onLike,
  onFork,
  onView,
  isLiked = false,
  className,
  viewOnly = false,
}) => {
  // Format creator name if available
  const creatorName = item.metadata?.creatorName || 'Anonymous';
  
  // Extract capabilities/tags for display
  const capabilities = Array.isArray(item.metadata?.capabilities) 
    ? item.metadata.capabilities.slice(0, 3) 
    : [];
    
  const tags = item.tags.slice(0, 3);
  
  // Format complexity badge if available
  const complexity = item.metadata?.complexity;
  
  // Get complexity color
  const getComplexityColor = (level?: string) => {
    switch(level) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-amber-100 text-amber-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleView = () => {
    if (onView) onView(item.id!);
  };

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onLike && !viewOnly) onLike(item.id!);
  };

  const handleFork = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onFork && !viewOnly) onFork(item.id!);
  };

  return (
    <Card 
      className={cn(
        "group overflow-hidden transition-all duration-300 hover:shadow-md h-full flex flex-col", 
        item.featured ? "border-amber-300" : "",
        className
      )}
    >
      <Link 
        to={`/showcase/${item.id}`} 
        className="flex flex-col h-full"
        onClick={handleView}
      >
        <CardHeader className="p-4 pb-0">
          <div className="flex justify-between items-start">
            <Avatar className="h-12 w-12 border">
              <AvatarImage src={item.thumbnailUrl || '/placeholder.svg'} alt={item.title} />
              <AvatarFallback>
                {item.title.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            {item.featured && (
              <Badge variant="outline" className="border-amber-300 text-amber-700">
                Featured
              </Badge>
            )}
          </div>
          
          <CardTitle className="mt-2 text-lg line-clamp-1">{item.title}</CardTitle>
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <span>By {creatorName}</span>
          </div>
          
          <CardDescription className="mt-2 line-clamp-2">
            {item.description}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="flex-grow p-4">
          <div className="flex flex-wrap gap-2 mt-1">
            {complexity && (
              <Badge variant="secondary" className={getComplexityColor(complexity)}>
                {complexity}
              </Badge>
            )}
            
            {capabilities.map((capability, index) => (
              <Badge key={`capability-${index}`} variant="outline" className="text-xs">
                {capability}
              </Badge>
            ))}
            
            {tags.map((tag, index) => (
              <Badge key={`tag-${index}`} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        </CardContent>
        
        <CardFooter className="p-4 pt-0 flex justify-between items-center">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Eye size={14} />
              <span>{item.views || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <Heart size={14} fill={isLiked ? "currentColor" : "none"} />
              <span>{item.likes || 0}</span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={handleFork}
              disabled={viewOnly}
            >
              <GitFork size={16} />
              <span className="sr-only">Fork</span>
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon" 
              className={cn(
                "h-8 w-8", 
                isLiked ? "text-red-500" : ""
              )}
              onClick={handleLike}
              disabled={viewOnly}
            >
              <Heart size={16} fill={isLiked ? "currentColor" : "none"} />
              <span className="sr-only">Like</span>
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleView}
            >
              <ExternalLink size={16} />
              <span className="sr-only">View</span>
            </Button>
          </div>
        </CardFooter>
      </Link>
    </Card>
  );
};